import React, { Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';
import { joinDefined, PAGE_CREMATIONS, prettyNumber, stringIsNullOrEmpty } from '../../utils/strings';
import Table, { Cell, HeaderRow, Row } from '../../components/table/Table';
import Inline, { inlineAlignment } from '../../components/layout/Inline';
import {
    getDeliveryDateTime,
    getFinalInsertionDate,
    niceTimeFromString,
    shortDateFromString,
    shortDateTimeFromString
} from '../../utils/date';
import AlertBar from '../../components/form/AlertBar';
import Button from '../../components/form/Button';
import { isRelatedObjectDefined } from '../../utils/bookable';

export default withStyles({})(({ variant, runsheetType, runsheetStatus, cremation, countA }) => {
    let cssVariant = null;
    if (!stringIsNullOrEmpty(variant)) cssVariant = `runsheet--${variant}`;
    const spacer = '(not set)';
    const deceased = cremation.Deceased || false;
    const deceasedName = !!deceased && joinDefined([deceased.FirstName, deceased.MiddleName, deceased.Surname], ' ');
    const recordID = cremation.ID;
    const legacyKey = cremation.LegacyKey;

    const assignedStaff = joinDefined(
        [cremation.CremationOperator.FirstName, cremation.CremationOperator.Surname],
        ' '
    );
    const witnessInsertion = 'Required' === cremation.WitnessOfInsertionRequired;
    const funeralService = cremation.FuneralService || cremation.Funeral.PlaceOfService.Type;
    const deliveryDateTime = shortDateTimeFromString(getDeliveryDateTime(cremation));
    const deceasedWeight = cremation.DeceasedWeight;
    const estimatedDeceasedWeight = cremation.EstimatedDeceasedWeight;
    const sameDay = !!cremation.SameDayCremation;
    const nextDay = !!cremation.NextDayCremation;
    const reflectionRoomAttendees = joinDefined(
        [
            !!cremation.ReflectionRoom.Applicant ? 'Applicant' : null,
            !!cremation.ReflectionRoom.AuthorisedContact ? 'Authorised Contact' : null,
            cremation.ReflectionRoom.AttendeeNames
        ],
        ', '
    );

    const renderReflectionRoomDetails = () => {
        return (
            !!parseInt(cremation.ReflectionRoom.ID) && (
                <div>
                    <h5>Viewing Room Booking:</h5>
                    <p>
                        <span className="info">When:&nbsp;</span>
                        {shortDateFromString(cremation.ReflectionRoom.Start)}
                        <> from</>
                        {(niceTimeFromString(cremation.ReflectionRoom.Start) || '(not set)') +
                            ' to ' +
                            (niceTimeFromString(cremation.ReflectionRoom.End) || '(not set)')}
                    </p>
                    {!!reflectionRoomAttendees && (
                        <p>
                            <span className="info">Attendees:&nbsp;</span>
                            {reflectionRoomAttendees}
                        </p>
                    )}
                </div>
            )
        );
    };

    const renderDispatches = () => !!cremation.DispatchDates.length && cremation.DispatchDates.map((dispatchDate, i) =>

        <div key={dispatchDate.ID}>
            <h5>
                Dispatch #{i + 1}
            </h5>
            {dispatchDate.DispatchComplete ? (<>
                    <p>
                        <span className="info">Completed.</span>
                    </p>
                    {dispatchDate.Ashes && (
                        <p>
                            <span className="info">Ashes:&nbsp;</span>
                            {dispatchDate.Ashes}
                        </p>
                    )}
                </>
            ) : (
                <>
                    <p>
                        <span className="info">Dispatch date:&nbsp;</span>
                        {shortDateFromString(dispatchDate.Date)}
                        {!!dispatchDate.Time && ', ' + niceTimeFromString(dispatchDate.Time)}
                    </p>
                    {dispatchDate.ContainmentStyle && (
                        <p>
                            <span className="info">Containment:&nbsp;</span>
                            {dispatchDate.ContainmentStyle}
                        </p>
                    )}
                    {dispatchDate.Ashes && (
                        <p>
                            <span className="info">Ashes:&nbsp;</span>
                            {dispatchDate.Ashes}
                        </p>
                    )}
                    <p>
                                                    <span
                                                        className="info">{dispatchDate.DispatchMethod || 'Collected by'}:&nbsp;</span>
                        {dispatchDate.AshesCollectedBy || spacer}
                    </p>
                    <p>
                        <span className="info">Assigned staff:&nbsp;</span>
                        {joinDefined(dispatchDate.AssignedStaff.map(obj => joinDefined([
                            obj.FirstName,
                            obj.Surname
                        ], ' ')), ', ') || spacer}
                    </p>
                    {dispatchDate.Comment && (
                        <p>
                            <span className="info">Comments:&nbsp;</span>
                            {dispatchDate.Comment}
                        </p>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className={`runsheet ${cssVariant || ''}`}>
            <Table thead={
                <thead>
                <HeaderRow pad>
                    <Cell colSpan={2}>
                        <Inline alignment={inlineAlignment.rightAlignSiblings} center>
                            <div>
                                <span className="runsheet-header">
                                    {runsheetType()}#{countA}&nbsp;{runsheetStatus}:&nbsp;
                                    {deceasedName}
                                </span>
                            </div>
                            <p className="header-record">
                                <Button variant="link-white" href={PAGE_CREMATIONS + `${legacyKey}/${recordID}`}>
                                    {legacyKey}
                                </Button>
                            </p>
                        </Inline>
                    </Cell>
                </HeaderRow>
                </thead>
            }>
                <Row pad>
                    <Cell>
                        <h5>Arrival</h5>
                        <p>
                            <span className="info">{!!cremation.DeliveryDate ? 'Arrived' : 'Expected'}:&nbsp;</span>
                            {deliveryDateTime}
                        </p>

                        <p>
                            <span className="info">Delivery by:&nbsp;</span>
                            {cremation.EstimatedDeliveredBy || spacer}
                        </p>

                        {(!!cremation.DeliveryDate || isRelatedObjectDefined(cremation.DeliveryReceivedBy)) && (
                            <p>
                                <span className="info">Received by:&nbsp;</span>
                                {joinDefined([
                                    cremation.DeliveryReceivedBy.FirstName,
                                    cremation.DeliveryReceivedBy.Surname
                                ], ' ') || spacer}
                            </p>
                        )}

                        {!!deceasedWeight
                            ? (
                                <p>
                                    <span className="info">Weight:&nbsp;</span>
                                    {prettyNumber(deceasedWeight)}&nbsp;kg
                                </p>
                            ) : (!!estimatedDeceasedWeight ? (
                                <p>
                                    <span className="info">(Est) Weight:&nbsp;</span>
                                    {prettyNumber(estimatedDeceasedWeight)}&nbsp;kg
                                </p>
                            ) : (
                                <p>
                                    <span className="info">Weight:&nbsp;</span>
                                    {spacer}
                                </p>
                            ))
                        }

                        {cremation.IsDeliveryOnly ? (
                            <p>
                                <span className="info">Funeral service:&nbsp;</span>
                                Delivery only
                            </p>
                        ) : (!!funeralService &&
                            <p>
                                <span className="info">Funeral service:&nbsp;</span>
                                {'Service' === funeralService ? ' Full Service' : funeralService}
                            </p>
                        )}

                        {renderReflectionRoomDetails()}
                    </Cell>

                    <Cell>
                        <h5>Insertion</h5>
                        <p>
                            <span className="info">Final Insertion:&nbsp;</span>
                            {shortDateFromString(sameDay
                                ? (getDeliveryDateTime(cremation))
                                : getFinalInsertionDate(getDeliveryDateTime(cremation))
                            )}
                        </p>

                        <p>
                            <span className="info">Operator:&nbsp;</span>
                            {assignedStaff || spacer}
                        </p>

                        <p>
                            <span className="info">Scheduled for:&nbsp;</span>
                            {(cremation.EstimatedInsertionTime
                                    ? shortDateTimeFromString(joinDefined([cremation.EstimatedInsertionDate, cremation.EstimatedInsertionTime], ' '))
                                    : shortDateFromString(cremation.EstimatedInsertionDate)
                            ) || spacer}
                        </p>

                        {!!cremation.InsertionDate && (
                            <p>
                                <span className="info">Started:&nbsp;</span>
                                {shortDateTimeFromString(joinDefined([cremation.InsertionDate, cremation.InsertionTime], ' ')) || spacer}
                            </p>
                        )}
                        {!!cremation.CremationComplete && (
                            <p>
                                <span className="info">Completed:&nbsp;</span>
                                {shortDateTimeFromString(joinDefined([cremation.CremationEndDate, cremation.CremationEndTime], ' ')) || spacer}
                            </p>
                        )}

                        {!!sameDay && (
                            <AlertBar variant="warning">
                                SAME-DAY CREMATION
                            </AlertBar>
                        )}
                        {!!nextDay && (
                            <AlertBar variant="info">
                                PRIORITY CREMATION
                            </AlertBar>
                        )}
                        {!!witnessInsertion && (
                            <AlertBar variant="fire">
                                WITNESS OF INSERTION
                            </AlertBar>
                        )}
                        {!cremation.ReadyForCremation ? (
                            <AlertBar variant="error">
                                This record is not marked as ready for cremation.
                            </AlertBar>
                        ) : (!!cremation.HasMissingCertificates && (
                            <AlertBar variant="warning">
                                This record is marked as ready for cremation, but has incomplete documentation.
                            </AlertBar>
                        ))}
                        {!!cremation.HadPacemaker && (!!cremation.HadPacemakerRemoved ? <AlertBar variant="info">
                            Pacemaker was removed.
                        </AlertBar> : <AlertBar variant="error">
                            Pacemaker was not removed!
                        </AlertBar>)}

                        {!!cremation.SpecialInstructions.length && (
                            <Fragment>
                                <h5>Special Instructions:&nbsp;</h5>
                                {cremation.SpecialInstructions.map((specialInstruction, idx) => (
                                    <div key={specialInstruction.ID}>
                                        {!!idx && <br />}
                                        <strong>{specialInstruction.Title}:&nbsp;</strong><br />
                                        <span className="comments">{specialInstruction.Comment}</span>
                                    </div>
                                ))}
                            </Fragment>
                        )}

                        {renderDispatches()}
                    </Cell>
                </Row>
            </Table>

        </div>
    );
});
